import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import * as styles from './ExtensionPostRow.module.scss';
import PostRowToolbar from './PostRowToolbar';
import PostTitleWithVerified from './PostTitleWithVerified';
import AvatarStack from '../../../components/AvatarStack';
import { MOD_KEY } from '../../../constants/os';
import type { DocumentType } from '../../../graphql/generated/gql';
import { gql } from '../../../graphql/generated/gql';
import PostBreadcrumbs from '../../../post/PostBreadcrumbs';
import PostIcon from '../../../post/PostIcon';
import NestableLink from '../../../utils/NestableLink';
import omitNonTopics from '../../../utils/omitNonTopics';
import postPath from '../../../utils/url/postPath';
import sendMessageToSlabFrame from '../sendMessageToSlabFrame';

const POST_FRAGMENT = gql(`
  fragment ExtensionPostRow_post on Post {
    id
    contributors {
      ...AvatarStack_users
    }
    topics {
      ...PostBreadcrumbs_topic
    }
    ...PostRowToolbar_post
    ...PostTitleWithVerified_post
    ...PostIcon_post
  }
`);

const HOVER_INTENT_TIMEOUT = 200;

interface PostRowProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  title?: ReactNode;
}

const PostRow = ({ post, title }: PostRowProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { contributors } = post;
  const [isHovering, setIsHovering] = useState(false);
  const isPersisted = false; // TODO

  useEffect(() => {
    if (!isHovering) return undefined;

    const timeout = setTimeout(() => {
      if (ref.current) {
        const bounds = ref.current.getBoundingClientRect();
        sendMessageToSlabFrame({
          'slab.type': 'hovercard.open',
          'slab.data': {
            id: post.id,
            verticalBounds: {
              top: bounds.top,
              bottom: bounds.bottom,
            },
          },
        });
      }
    }, HOVER_INTENT_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [isHovering, post.id]);

  const topic = omitNonTopics(post.topics)[0];
  return (
    <NestableLink
      ref={ref}
      className={classNames(styles.container, {
        [styles.persisted]: isPersisted,
      })}
      to={postPath(post)}
      onClick={(event) => {
        if (!event[MOD_KEY]) {
          event.preventDefault();

          sendMessageToSlabFrame({
            'slab.type': 'hovercard.persist',
            'slab.data': {
              id: post.id,
              preferredTop: ref.current?.getBoundingClientRect().top ?? 0,
            },
          });
          setIsHovering(false);
        }
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className={styles.postRow}>
        <div className={styles.iconContainer}>
          <PostIcon post={post} />
        </div>
        <div
          className={classNames(styles.titleRow, {
            [styles.noTopics]: !topic,
          })}
        >
          {topic && (
            <span className={styles.topics}>
              <PostBreadcrumbs topic={topic} truncate />
            </span>
          )}
          <span className={styles.title}>
            {title || <PostTitleWithVerified post={post} />}
          </span>
        </div>
        <div className={styles.contributors}>
          <AvatarStack items={(contributors || []).slice(0, 3)} />
        </div>
        {isHovering && (
          <div className={styles.toolbar}>
            <PostRowToolbar post={post} postRowRef={ref} />
          </div>
        )}
      </div>
    </NestableLink>
  );
};

export default PostRow;
